// Notification sound utility using Web Audio API (no external asset needed)
let audioCtx = null;

function ensureCtx() {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      console.log("[Sound] AudioContext created");
    } catch (e) {
      console.error("[Sound] Failed to create AudioContext:", e);
      return null;
    }
  }
  if (audioCtx.state === "suspended") {
    console.log("[Sound] Resuming suspended AudioContext");
    audioCtx.resume().catch((e) => console.error("[Sound] Failed to resume:", e));
  }
  return audioCtx;
}

// Warm two-tone chime: C5 → E5, short
export function playNewOrderChime() {
  try {
    const ctx = ensureCtx();
    if (!ctx) {
      console.warn("[Sound] AudioContext not available for order chime");
      return;
    }
    console.log("[Sound] Playing order chime");
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.connect(ctx.destination);

    const tones = [
      { freq: 880, start: 0.0, dur: 0.18 },   // A5
      { freq: 1174.66, start: 0.18, dur: 0.28 }, // D6
    ];

    tones.forEach((t) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(t.freq, now + t.start);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now + t.start);
      g.gain.linearRampToValueAtTime(0.35, now + t.start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + t.start + t.dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur + 0.05);
    });
  } catch (e) {
    console.error("[Sound] Error playing order chime:", e);
  }
}

// Urgent waiter call notification: triple bell sound, louder and more distinct
export function playWaiterCallAlert() {
  try {
    const ctx = ensureCtx();
    if (!ctx) {
      console.warn("[Sound] AudioContext not available for waiter alert");
      return;
    }
    console.log("[Sound] Playing waiter call alert");
    const now = ctx.currentTime;
    
    // Play 3 urgent bell sounds
    for (let rep = 0; rep < 3; rep++) {
      const startTime = now + (rep * 0.35);
      
      // Create bell-like tone using higher frequency
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(1320, startTime);  // E6 - higher and more urgent
      
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, startTime);
      g.gain.linearRampToValueAtTime(0.6, startTime + 0.02);  // Louder
      g.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25);
      
      osc.connect(g).connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + 0.3);
    }
  } catch (e) {
    console.error("[Sound] Error playing waiter alert:", e);
  }
}

export function unlockAudio() {
  try {
    const ctx = ensureCtx();
    if (ctx) {
      console.log("[Sound] Audio unlocked, state:", ctx.state);
      // If suspended, try to resume
      if (ctx.state === "suspended") {
        ctx.resume().then(() => {
          console.log("[Sound] Audio context resumed successfully");
        }).catch((e) => {
          console.error("[Sound] Failed to resume audio context:", e);
        });
      }
    }
  } catch (e) {
    console.error("[Sound] Error unlocking audio:", e);
  }
}

// Test function to verify audio is working
export function testAudio() {
  try {
    console.log("[Sound] Testing audio...");
    const ctx = ensureCtx();
    if (!ctx) {
      console.error("[Sound] AudioContext not available");
      return false;
    }
    console.log("[Sound] AudioContext state:", ctx.state);
    console.log("[Sound] AudioContext sampleRate:", ctx.sampleRate);
    
    // Play a simple test tone
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.frequency.setValueAtTime(440, now);
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.5);
    
    console.log("[Sound] Test tone played");
    return true;
  } catch (e) {
    console.error("[Sound] Error testing audio:", e);
    return false;
  }
}
